<template>
  <div
    v-if="hasInvoices"
    class="overdue-alert mb-4"
  >
    <div class="overdue-alert-head">
      <i class="fa fa-file-text-o overdue-alert-icon" />
      <div class="overdue-alert-headline">
        <span class="overdue-alert-title">{{ report.invoiceCountLabel }}</span>
        <span class="overdue-alert-subtitle">{{ report.maxDaysOverdueLabel }}</span>
      </div>
      <button
        type="button"
        class="overdue-alert-toggle"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Ocultar detalle' : 'Ver detalle' }}
        <i
          class="fa"
          :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </button>
    </div>

    <div
      v-if="expanded"
      class="overdue-alert-detail"
    >
      <table class="overdue-table">
        <thead>
          <tr>
            <th>Folio</th>
            <th>Tipo</th>
            <th>Emisión</th>
            <th>Vence</th>
            <th class="text-center">
              Días
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invoice in report.invoices"
            :key="invoice.id"
          >
            <td>{{ invoice.folio }}</td>
            <td>{{ invoice.dteTypeName }}</td>
            <td>{{ invoice.formattedIssuedAt }}</td>
            <td>{{ invoice.formattedDueAt }}</td>
            <td class="text-center">
              <span
                class="days-badge"
                :class="invoice.daysOverdueClass"
              >{{ invoice.daysOverdue }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        v-if="report.isPartialPage"
        class="overdue-table-foot"
      >
        Mostrando las {{ report.invoices.length }} facturas con mayor atraso de {{ report.total }}.
      </p>
    </div>
  </div>
</template>

<script>
import { finance } from '@/services/api/ExpertoRetail'
import OverdueInvoicesReport from '@/app/shared/models/OverdueInvoicesReport'

// The endpoint sorts by days overdue descending by default, so the first page
// already holds the most urgent invoices.
const PAGE_SIZE = 50

export default {
  name: 'OverdueInvoicesAlert',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      report: null,
      expanded: false,
      // A 403 (not a root user) or a 404 (endpoint not deployed yet) means the
      // section is simply not available for this user, so it is not requested
      // again while the component stays mounted.
      unavailable: false,
      loading: false
    }
  },
  computed: {
    hasInvoices () {
      return !!this.report && this.report.hasInvoices
    }
  },
  watch: {
    show: {
      immediate: true,
      handler (val) {
        if (val) {
          this.loadOverdueInvoices()
        } else {
          this.expanded = false
        }
      }
    }
  },
  methods: {
    async loadOverdueInvoices () {
      if (this.unavailable || this.loading) return

      this.loading = true
      try {
        const data = await finance.overdueInvoices({ page_size: PAGE_SIZE })
        this.report = new OverdueInvoicesReport(data)
      } catch (error) {
        this.handleLoadError(error)
      }
      this.loading = false
    },
    handleLoadError (error) {
      // api-client rejects a 403 with the axios error and any other status
      // with the response itself, so both shapes are checked.
      const status = (error && error.response && error.response.status) || (error && error.status)

      this.report = null
      if (status === 403 || status === 404) {
        this.unavailable = true
        return
      }
      console.error(error)
    }
  }
}
</script>

<style scoped>
.overdue-alert {
  background: #fffaf0;
  border: 1px solid #fbd38d;
  border-left: 4px solid #dd6b20;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.overdue-alert-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.overdue-alert-icon {
  color: #dd6b20;
}

.overdue-alert-headline {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.overdue-alert-title {
  font-weight: 600;
  color: #7b341e;
}

.overdue-alert-subtitle {
  font-size: 0.85rem;
  color: #975a16;
}

.overdue-alert-toggle {
  border: 1px solid #dd6b20;
  background: #ffffff;
  color: #9c4221;
  border-radius: 20px;
  padding: 0.35rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.overdue-alert-toggle:hover {
  background: #dd6b20;
  color: #ffffff;
}

.overdue-alert-detail {
  margin-top: 1rem;
  background: #ffffff;
  border: 1px solid #fbd38d;
  border-radius: 8px;
  overflow-x: auto;
}

.overdue-table {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
}

.overdue-table th {
  background: #fffaf0;
  color: #975a16;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  padding: 0.75rem;
  white-space: nowrap;
}

.overdue-table td {
  padding: 0.75rem;
  border-top: 1px solid #fef5e7;
  color: #2d3748;
  font-size: 0.875rem;
  white-space: nowrap;
}

.days-badge {
  display: inline-block;
  min-width: 2.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.days-badge-moderate {
  background: #fefcbf;
  color: #744210;
}

.days-badge-high {
  background: #feebc8;
  color: #9c4221;
}

.days-badge-severe {
  background: #fed7d7;
  color: #822727;
}

.overdue-table-foot {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #975a16;
  border-top: 1px solid #fef5e7;
}
</style>



// WEBPACK FOOTER //
// src/app/nav_bar/components/OverdueInvoicesAlert.vue